// Settings (§14.4): Audio, Graphics, Gameplay, Controls, Accessibility and Profile tabs rendered
// from WP9a's SETTINGS_SCHEMA. Every change applies live and persists (the settings store emits
// 'settings'; audio, renderer, input, touch layout, HUD and UI scale all listen). Controls has the
// rebinding tables: two slots per keyboard action and up to two pad buttons, captured with "Press
// a key…" (Esc cancels, Delete clears), with a note when a key moved from another action.
import {
  KEY_ACTION_LABELS, KEY_ACTION_ORDER, PAD_ACTION_LABELS, PAD_ACTION_ORDER, RESERVED_KEY, rebindKey, rebindPad,
  type KeyAction, type PadAction,
} from '../../input/bindings';
import { keyLabel, padButtonLabel } from '../../input/glyphs';
import { getSetting, SETTINGS_SCHEMA, type SettingRow, type SettingsGroupId } from '../../profile/settings';
import type { LiveScreen, NavAction } from '../contracts';
import type { UiDeps } from '../deps';
import { glyphCap, padButtonCap } from '../glyphView';
import { icon } from '../icons';
import { el, replayClass, setText } from '../ui';
import { button, choice, closeButton, field, setLabel, slider, tabs, toggle, type Control } from '../widgets';

const TAB_ICONS: Readonly<Record<SettingsGroupId, string>> = {
  audio: 'speaker', graphics: 'monitor', gameplay: 'gamepad', controls: 'keyboard', accessibility: 'access', profile: 'profile',
};
const QUALITY_NAMES: Readonly<Record<string, string>> = { high: 'High', medium: 'Medium', low: 'Low', potato: 'Potato' };
/** Key actions the rebinding table hides (Menu stays on Escape, §8.1). */
const HIDDEN_KEY_ACTIONS: ReadonlySet<KeyAction> = new Set<KeyAction>(['menu']);

export interface SettingsProps { tab?: SettingsGroupId }

export function create(deps: UiDeps): LiveScreen {
  const store = deps.services.settings;
  const input = deps.services.input;
  let group: SettingsGroupId = 'audio';
  let controls: { path: string; ctl: Control<unknown> }[] = [];
  let capturing: HTMLButtonElement | null = null;
  const confirms = new Set<string>();

  const body = el('div', { class: 'sheet-body set-body', attrs: { role: 'tabpanel' } });
  const note = el('div', { class: 'set-note', attrs: { 'aria-live': 'polite' } });
  const tabStrip = tabs('Settings', SETTINGS_SCHEMA.map((g) => ({ id: g.id, label: g.label, icon: TAB_ICONS[g.id] })), (id) => {
    group = id as SettingsGroupId;
    render();
  });
  tabStrip.el.setAttribute('data-tabs', '');
  const resetTab = button({ label: 'RESET TAB', icon: 'rematch', variant: 'cream', onClick: () => doResetTab() });
  const done = button({ label: 'DONE', icon: 'check', variant: 'pink', onClick: () => deps.nav.back() });
  const panel = el('div', { class: 'panel sheet with-ribbon settings-panel springy' },
    el('div', { class: 'ribbon sky', text: 'SETTINGS' }), closeButton(() => deps.nav.back()),
    tabStrip.el, body, note,
    el('div', { class: 'sheet-foot row center wrap' }, resetTab, done));
  const root = el('div', { class: 'screen modal' }, panel);

  function say(text: string): void {
    setText(note, text);
    replayClass(note, 'pop');
  }

  function rowControl(row: SettingRow): HTMLElement | null {
    const s = store.get();
    switch (row.kind) {
      case 'slider': {
        const v = getSetting(s, row.path) as number;
        const fmt = row.unit === '%' ? (n: number) => `${Math.round(n)}%` : (n: number) => n.toFixed(2);
        const ctl = slider(row.label, { min: row.min, max: row.max, step: row.step, value: v, format: fmt, onChange: (n) => store.set(row.path, n) });
        controls.push({ path: row.path, ctl: ctl as Control<unknown> });
        return field(row.label, ctl.el, row.hint);
      }
      case 'toggle': {
        const ctl = toggle(row.label, getSetting(s, row.path) as boolean, (b) => {
          store.set(row.path, b);
          deps.services.playUi('click');
        });
        controls.push({ path: row.path, ctl: ctl as Control<unknown> });
        return field(row.label, ctl.el, row.hint);
      }
      case 'choice': {
        const options = row.options.map((o) => ({ value: o.value, label: row.path === 'graphics.quality' && o.value === 'auto' ? `Auto (${QUALITY_NAMES[deps.services.quality()] ?? 'High'})` : o.label }));
        const ctl = choice(row.label, options, getSetting(s, row.path) as string, (v) => {
          store.set(row.path, v);
          deps.services.playUi('click');
        });
        controls.push({ path: row.path, ctl: ctl as Control<unknown> });
        return field(row.label, ctl.el, row.hint);
      }
      case 'bindings':
        return row.device === 'kbm' ? keyTable(row.label) : padTable(row.label);
      case 'action':
        return actionRow(row);
      default:
        return null;
    }
  }

  // ---- Rebinding -----------------------------------------------------------------------------

  function slotButton(content: HTMLElement, aria: string, onPress: (b: HTMLButtonElement) => void): HTMLButtonElement {
    const b = el('button', { class: 'bind-slot', attrs: { type: 'button', 'aria-label': aria } }, content);
    b.addEventListener('click', () => onPress(b));
    return b;
  }

  function keyCapFor(code: string | null): HTMLElement {
    if (code === null) return el('span', { class: 'bind-empty', text: '—' });
    return glyphCap({ device: 'kbm', kind: code.startsWith('Mouse') ? 'mouse' : 'key', label: keyLabel(code), text: `[${keyLabel(code)}]` });
  }

  async function captureKey(b: HTMLButtonElement, action: KeyAction, slot: 0 | 1): Promise<void> {
    if (capturing) return;
    capturing = b;
    b.classList.add('capturing');
    b.replaceChildren(el('span', { class: 'bind-wait', text: 'Press a key…' }));
    say('Press a key or mouse button • Esc cancels • Delete clears');
    const code = await input.capture('key');
    capturing = null;
    if (code === null || typeof code !== 'string') {
      say('Cancelled');
      render();
      return;
    }
    const value = code === 'Delete' ? null : code;
    const r = rebindKey(input.bindings.get(), action, slot, value);
    if (!r.ok) {
      say(code === RESERVED_KEY ? 'Esc is reserved for the menu' : 'That key cannot be used');
      deps.services.playUi('error');
    } else {
      input.bindings.set(r.bindings);
      deps.services.playUi('coin');
      say(value === null ? `${KEY_ACTION_LABELS[action]}: slot cleared`
        : r.displaced ? `${keyLabel(value)} moved from ${KEY_ACTION_LABELS[r.displaced.action]} to ${KEY_ACTION_LABELS[action]}` : `${KEY_ACTION_LABELS[action]}: ${keyLabel(value)}`);
    }
    render();
    focusBinding(`k:${action}:${slot}`);
  }

  async function capturePad(b: HTMLButtonElement, action: PadAction, slot: 0 | 1): Promise<void> {
    if (capturing) return;
    capturing = b;
    b.classList.add('capturing');
    b.replaceChildren(el('span', { class: 'bind-wait', text: 'Press a button…' }));
    say('Press a gamepad button • Esc cancels');
    const btn = await input.capture('pad');
    capturing = null;
    if (btn === null || typeof btn !== 'number') {
      say('Cancelled');
      render();
      return;
    }
    const r = rebindPad(input.bindings.get(), action, slot, btn);
    if (r.ok) {
      input.bindings.set(r.bindings);
      deps.services.playUi('coin');
      const lbl = padButtonLabel(btn, input.padFamily);
      say(r.displaced ? `${lbl} moved from ${PAD_ACTION_LABELS[r.displaced.action]} to ${PAD_ACTION_LABELS[action]}` : `${PAD_ACTION_LABELS[action]}: ${lbl}`);
    } else {
      deps.services.playUi('error');
    }
    render();
    focusBinding(`p:${action}:${slot}`);
  }

  function focusBinding(key: string): void {
    body.querySelector<HTMLElement>(`[data-bind="${key}"]`)?.focus({ preventScroll: true });
  }

  function keyTable(label: string): HTMLElement {
    const b = input.bindings.get();
    const rows = KEY_ACTION_ORDER.filter((a) => !HIDDEN_KEY_ACTIONS.has(a)).map((a) => {
      const slots = ([0, 1] as const).map((slot) => {
        const btn = slotButton(keyCapFor(b.keys[a][slot]), `${KEY_ACTION_LABELS[a]} ${slot === 0 ? 'primary' : 'secondary'}`, (node) => void captureKey(node, a, slot));
        btn.dataset.bind = `k:${a}:${slot}`;
        return btn;
      });
      return el('div', { class: 'bind-row' }, el('span', { class: 'bind-label', text: KEY_ACTION_LABELS[a] }), ...slots);
    });
    return el('div', { class: 'bind-table' },
      el('div', { class: 'bind-head' }, icon('keyboard'), el('span', { text: label }), el('small', { text: 'Primary' }), el('small', { text: 'Secondary' })), ...rows);
  }

  function padTable(label: string): HTMLElement {
    const b = input.bindings.get();
    const fam = input.padFamily;
    const rows = PAD_ACTION_ORDER.map((a) => {
      const slots = ([0, 1] as const).map((slot) => {
        const btnIdx = b.pad[a][slot];
        const cap = btnIdx === undefined ? el('span', { class: 'bind-empty', text: '—' }) : padButtonCap(btnIdx, fam);
        const btn = slotButton(cap, `${PAD_ACTION_LABELS[a]} button ${slot + 1}`, (node) => void capturePad(node, a, slot));
        btn.dataset.bind = `p:${a}:${slot}`;
        return btn;
      });
      return el('div', { class: 'bind-row' }, el('span', { class: 'bind-label', text: PAD_ACTION_LABELS[a] }), ...slots);
    });
    return el('div', { class: 'bind-table pad' },
      el('div', { class: 'bind-head' }, icon('gamepad'), el('span', { text: label }), el('small', { text: input.padConnected ? 'Connected' : 'No pad yet' }), el('small', { text: '' })), ...rows,
      el('div', { class: 'bind-fixed' }, el('span', { text: 'Menus always use: ' }), glyphCap(input.glyph('confirm', 'pad')), el('span', { text: 'select ' }), glyphCap(input.glyph('back', 'pad')), el('span', { text: 'back ' }),
        glyphCap(input.glyph('tabL', 'pad')), glyphCap(input.glyph('tabR', 'pad')), el('span', { text: 'tabs' })));
  }

  function actionRow(row: Extract<SettingRow, { kind: 'action' }>): HTMLElement {
    const id = row.id;
    const armed = confirms.has(id);
    const variant = row.danger ? 'danger' : 'cream';
    const label = armed ? (id === 'resetProgress' ? 'YES, RESET EVERYTHING' : 'CONFIRM') : row.label.toUpperCase();
    const b = button({ label, icon: id === 'testRumble' ? 'gamepad' : id === 'editName' ? 'profile' : 'rematch', variant, size: 'small', onClick: () => {
      if (id === 'editName') {
        deps.nav.show('name', { edit: true });
        return;
      }
      if (id === 'testRumble') {
        input.rumble('heavy');
        say(input.padConnected ? 'Brrrr!' : 'Connect a gamepad to feel it');
        return;
      }
      if (!confirms.has(id)) {
        confirms.add(id);
        render();
        focusAction(id);
        return;
      }
      confirms.delete(id);
      if (id === 'resetBindings') {
        input.bindings.reset();
        say('Controls reset to the defaults');
      } else if (id === 'resetProgress') {
        deps.services.profiles.resetProgress();
        say('Progress reset. Fresh bean!');
      }
      deps.services.playUi('coin');
      render();
    } });
    b.dataset.action = id;
    if (armed) b.classList.add('armed');
    return field(row.label, b, row.hint);
  }

  function focusAction(id: string): void {
    body.querySelector<HTMLElement>(`[data-action="${id}"]`)?.focus({ preventScroll: true });
  }

  /** The device a Controls row belongs to: keyboard & mouse, gamepad or touch. */
  function rowDevice(r: SettingRow): 'kbm' | 'pad' | 'touch' {
    if (r.kind === 'bindings') return r.device === 'kbm' ? 'kbm' : 'pad';
    if (r.kind === 'action') return r.id === 'testRumble' ? 'pad' : 'kbm';
    return r.path.startsWith('controls.touch.') ? 'touch' : r.path.startsWith('controls.pad.') ? 'pad' : 'kbm';
  }

  /** Controls: the device in use first (a phone opens on its touch options, not the key table). */
  function controlsInDeviceOrder(rows: readonly SettingRow[]): SettingRow[] {
    const first = input.device;
    const order = [first, ...(['kbm', 'pad', 'touch'] as const).filter((d) => d !== first)];
    return order.flatMap((d) => rows.filter((r) => rowDevice(r) === d));
  }

  function render(): void {
    controls = [];
    const g = SETTINGS_SCHEMA.find((x) => x.id === group) ?? SETTINGS_SCHEMA[0];
    const rows = (group === 'controls' ? controlsInDeviceOrder(g.rows) : g.rows).map((r) => rowControl(r)).filter((n): n is HTMLElement => n !== null);
    if (group === 'profile') {
      const p = deps.profile();
      rows.unshift(el('div', { class: 'set-profile' }, icon('profile'), el('b', { text: p.name || 'New Bean' }), el('span', { class: 'chip ink', text: `LV ${p.level}` })));
    }
    if (group === 'controls') rows.unshift(el('div', { class: 'set-device' }, el('span', { text: 'Using: ' }), el('b', { text: input.device === 'pad' ? 'Gamepad' : input.device === 'touch' ? 'Touch' : 'Keyboard & mouse' })));
    body.replaceChildren(el('div', { class: 'set-group' }, ...rows));
    body.scrollTop = 0;
    setLabel(resetTab, group === 'controls' ? 'RESET CONTROLS' : 'RESET TAB');
    resetTab.hidden = group === 'profile';
  }

  function doResetTab(): void {
    if (group === 'controls') {
      input.bindings.reset();
      store.reset('controls');
    } else if (group !== 'profile') {
      store.reset(group);
    }
    deps.services.playUi('back');
    say('Defaults restored');
    render();
  }

  deps.bus.on('settings', (s) => {
    if (root.hidden) return;
    for (const c of controls) c.ctl.set(getSetting(s, c.path as Parameters<typeof getSetting>[1]), true);
  });

  return {
    el: root,
    onShow(props) {
      const p = props as SettingsProps | undefined;
      if (p?.tab) group = p.tab;
      confirms.clear();
      setText(note, '');
      tabStrip.select(group);
      render();
    },
    onHide() {
      if (capturing) input.cancelCapture();
    },
    onNav(action: NavAction) {
      if (capturing) return true;
      if (action === 'tabL' || action === 'tabR') {
        tabStrip.step(action === 'tabL' ? -1 : 1);
        return true;
      }
      return false;
    },
  };
}
